import { NestedTreeControl } from "@angular/cdk/tree";
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { MatTreeNestedDataSource } from "@angular/material/tree";
import { Subscription } from "rxjs";

import {
  BrandCampaign,
  BrandFacebookTargetingType,
} from "../../../../shared/models/brandCampaign";
import {
  FacebookTargetingCategory,
  FacebookTargetingCategoryCode,
  FacebookTargetingType,
} from "../../../../shared/models/facebookTargeting";
import { FacebookTargetingService } from "../../shared/services/facebook-targeting.service";
import { LanguageService } from "../../../../shared/services/language.service";
import { cloneWith } from "../../../../shared/services/clonable";

class FacebookTargetingNode {
  constructor(
    public name: string,
    public code: FacebookTargetingCategoryCode,
    public type: string,
    public classType: string,
    public searchKey: string,
    public loadingFacebookTargetingTypes: boolean,
    public facebookTargetingTypes: FacebookTargetingType[],
    public selectedChildren: BrandFacebookTargetingType[],
  ) {}
}

@Component({
  selector: "app-brand-campaign-facebook-targeting-picker",
  templateUrl: "./brand-campaign-facebook-targeting-picker.component.html",
  styleUrl: "./brand-campaign-facebook-targeting-picker.component.scss",
})
export class BrandCampaignFacebookTargetingPickerComponent implements OnInit {
  @Input({ required: true }) public campaign!: BrandCampaign;
  @Input({ required: true })
  public facebookTargetingCategories!: FacebookTargetingCategory[];

  @Output()
  public facebookTargetingUpdated = new EventEmitter<void>();

  protected readonly treeControl = new NestedTreeControl<FacebookTargetingNode>(
    (node: any) => node.children,
  );
  protected readonly dataSource =
    new MatTreeNestedDataSource<FacebookTargetingCategory>();

  private behaviors: FacebookTargetingType[] = [];
  private demographics: FacebookTargetingType[] = [];
  private ongoingRequest?: Subscription;

  constructor(
    private readonly facebookTargetingService: FacebookTargetingService,
    private readonly languageService: LanguageService,
  ) {}

  public ngOnInit(): void {
    this.initData();

    this.languageService.changed$.subscribe(() => {
      this.initData();
    });
  }

  private initData(): void {
    const nodes: FacebookTargetingNode[] = [];

    this.behaviors = [];
    this.demographics = [];

    this.facebookTargetingCategories.forEach((category) => {
      const node = new FacebookTargetingNode(
        category.name,
        category.code,
        category.type,
        category.classType,
        "",
        false,
        [],
        [],
      );

      switch (category.code) {
        case FacebookTargetingCategoryCode.Behaviors:
          this.initChildrenData(node, this.behaviors);
          break;
        case FacebookTargetingCategoryCode.Demographics:
          this.initChildrenData(node, this.demographics);
          break;
        case FacebookTargetingCategoryCode.Interests:
          this.initInterestsData(node);
          break;
      }

      nodes.push(node);
    });

    this.dataSource.data = nodes;
  }

  private initChildrenData(
    node: FacebookTargetingNode,
    typeArray: FacebookTargetingType[],
  ): void {
    this.facebookTargetingService
      .getFacebookTargetingTypes(node.type, "", node.classType)
      .subscribe((facebookTargetingTypes: FacebookTargetingType[]) => {
        Object.assign(typeArray, facebookTargetingTypes);
        this.assignChildren(node, facebookTargetingTypes);
      });
  }

  private assignChildren(
    node: FacebookTargetingNode,
    facebookTargetingTypes: FacebookTargetingType[],
  ): void {
    this.campaign.facebookTargetingTypes.forEach(
      (brandFacebookTargetingType) => {
        if (brandFacebookTargetingType.code === node.code) {
          const translatedTargetType = facebookTargetingTypes.find(
            (t) => t.id === brandFacebookTargetingType.id,
          );
          brandFacebookTargetingType.name = translatedTargetType?.name ?? "";
          node.selectedChildren.push(brandFacebookTargetingType);
        }
      },
    );
  }

  private initInterestsData(node: FacebookTargetingNode): void {
    const interestsIds: string[] = [];

    this.campaign.facebookTargetingTypes.forEach(
      (brandFacebookTargetingType) => {
        if (brandFacebookTargetingType.code === node.code) {
          interestsIds.push(brandFacebookTargetingType.id);
        } else if (
          brandFacebookTargetingType.code !==
            FacebookTargetingCategoryCode.Demographics &&
          brandFacebookTargetingType.code !==
            FacebookTargetingCategoryCode.Behaviors
        ) {
          // Retro-compatibility with legacy targeting types (pre-2020)
          const found = node.selectedChildren.find(
            (n) => n.id === brandFacebookTargetingType.id,
          );
          if (!found) {
            node.selectedChildren.push(brandFacebookTargetingType);
          }
        }
      },
    );

    if (!interestsIds.length) {
      return;
    }

    this.facebookTargetingService
      .getInterestsList(interestsIds)
      .subscribe((facebookTargetingTypes) => {
        this.assignChildren(node, facebookTargetingTypes);
      });
  }

  protected hasChild = (_: number, node: any) =>
    !!node.children && node.children.length > 0;

  protected addFacebookTargeting(
    node: FacebookTargetingNode,
    facebookTargetingType: FacebookTargetingType,
  ): void {
    const brandFacebookTargetingType = cloneWith(
      new BrandFacebookTargetingType(),
      {
        id: facebookTargetingType.id,
        name: facebookTargetingType.name,
        type: facebookTargetingType.type,
        classType: node.classType,
        code: node.code,
      },
    );

    if (
      !this.campaign.isEditable ||
      node.selectedChildren.filter(
        (selectedTarget) => selectedTarget.id === facebookTargetingType.id,
      ).length > 0
    ) {
      this.deleteFacebookTargeting(node, brandFacebookTargetingType);
      return;
    }

    node.selectedChildren.push(brandFacebookTargetingType);

    this.campaign.facebookTargetingTypes.push(brandFacebookTargetingType);
    this.facebookTargetingUpdated.emit();
  }

  protected deleteFacebookTargeting(
    node: FacebookTargetingNode,
    facebookTargetingType: BrandFacebookTargetingType,
  ): void {
    if (!this.campaign.isEditable) {
      return;
    }

    node.selectedChildren.map((type, i) => {
      if (type.id === facebookTargetingType.id) {
        node.selectedChildren.splice(i, 1);
      }
    });

    this.campaign.facebookTargetingTypes.map((type, i) => {
      if (type.id === facebookTargetingType.id) {
        this.campaign.facebookTargetingTypes.splice(i, 1);
        this.facebookTargetingUpdated.emit();
      }
    });
  }

  private filterTargetingBehaviors(
    node: FacebookTargetingNode,
    key: string,
  ): void {
    node.facebookTargetingTypes = this.behaviors.filter((behavior) => {
      if (!key) {
        return false;
      }

      const behaviorFormatted = behavior.name.toLowerCase().replace(" ", "");
      const formattedKey = key.toLowerCase().replace(" ", "");

      return behaviorFormatted.includes(formattedKey);
    });
  }

  protected loadSearchTargets(node: FacebookTargetingNode, key: string): void {
    node.searchKey = key;

    if (node.code === FacebookTargetingCategoryCode.Interests) {
      return this.loadFacebookTargetingTypes(node);
    } else if (node.code === FacebookTargetingCategoryCode.Behaviors) {
      return this.filterTargetingBehaviors(node, key);
    } else if (node.code === FacebookTargetingCategoryCode.Demographics) {
      return this.filterTargetingDemographics(node, key);
    }
  }

  private filterTargetingDemographics(
    node: FacebookTargetingNode,
    key: string,
  ): void {
    node.facebookTargetingTypes = this.demographics.filter((demographic) => {
      if (!key) {
        return false;
      }

      const demographicFormatted = demographic.name
        .toLowerCase()
        .replace(" ", "");
      const formattedKey = key.toLowerCase().replace(" ", "");

      return demographicFormatted.includes(formattedKey);
    });
  }

  protected targetingTypeIsSelected(
    selectedChildren: BrandFacebookTargetingType[],
    target: FacebookTargetingType,
  ): boolean {
    return (
      selectedChildren.filter((selectedType) => target.id === selectedType.id)
        .length > 0
    );
  }

  private loadFacebookTargetingTypes(node: FacebookTargetingNode): void {
    node.loadingFacebookTargetingTypes = true;

    setTimeout(() => {
      this.ongoingRequest?.unsubscribe();
      this.ongoingRequest = this.facebookTargetingService
        .getFacebookTargetingTypes(node.type, node.searchKey, node.classType)
        .subscribe((facebookTargetingTypes) => {
          node.loadingFacebookTargetingTypes = false;
          node.facebookTargetingTypes = facebookTargetingTypes;
        });
    }, 1000);
  }

  protected clearNodeSearch(node: FacebookTargetingNode): void {
    node.searchKey = "";
    node.facebookTargetingTypes = [];
  }
}
